<template>
  <div
    class="admin-home"
    v-loading.fullscreen.lock="isLoading"
  >
    <!-- ステータスタブと新規注文作成 -->
    <AdminHomeHeader
      :status.sync="status"
      :orderCountByStatus="orderCountByStatus"
      @onChangeStatus="onChangeStatus"
    />

    <!-- 件数とフィルタ -->
    <AdminHomeSub
      :total="total"
      :current-page="currentPage"
      :page-size="pageSize"
      :is-show-filter-modal.sync="isShowFilterModal"
      :is-filtered="isFiltered"
      @onClearFilterConditions="onClearFilterConditions"
    >
      <AdminHomeFilter
        ref="filter"
        slot="filterModal"
        :status="status"
        :is-show-filter-modal.sync="isShowFilterModal"
        :is-filtered="isFiltered"
        @onChangeStatus="onChangeStatus"
        @onResetFilterConditions="onResetFilterConditions"
        @onChangeFilterConditions="onChangeFilterConditions"
      />
    </AdminHomeSub>

    <!-- 注文一覧 -->
    <AdminHomeOrders
      ref="adminHomeOrders"
      :status="status"
      :orders="orders"
      :sortParams="sortParams"
      @keepHomeState="keepHomeState"
      @updateSortDirection="updateSortDirection"
    />

    <!-- ページャ -->
    <AdminHomePagination
      :total="total"
      :total-page="totalPage"
      :page-size="pageSize"
      :current-page.sync="currentPage"
      @onChangeCurrentPage="onChangeCurrentPage"
    />
  </div>
</template>

<script>
import _ from "lodash";
import OrderUsecase from "@/usecases/order.usecase";
import { apiErrorHandler } from "@/services/apiErrorHandler";
import AdminHomeHeader from "@/components/Admin/Home/Header.vue";
import AdminHomeSub from "@/components/Admin/Home/Sub.vue";
import AdminHomeFilter from "@/components/Admin/Home/Filter.vue";
import AdminHomeOrders from "@/components/Admin/Home/Orders.vue";
import AdminHomePagination from "@/components/Admin/Home/Pagination.vue";

const PAGE_SIZE = 50;
const BASE_FILTER_CONDITIONS = {
  status: null,
};
const NOTIFY_MESSAGE_UNAVAILABLE_USER = {
  title: "エラーが発生しました",
  message: "有効なユーザではありません",
};

const ORDER_STATUSES = [
  // "DRAFT",
  // "ORDER_REQUEST",
  "PROCESSING",
  "PROCESSING_CREATING_LAYOUT",
  "REVISING_LAYOUT",
  "CONFIRMED",
  // "SHIPPED",
];

export default {
  components: {
    AdminHomeHeader,
    AdminHomeSub,
    AdminHomeFilter,
    AdminHomeOrders,
    AdminHomePagination,
  },
  data () {
    return {
      status: "ALL",
      total: 0,
      totalPage: 1,
      pageSize: PAGE_SIZE,
      currentPage: 1,
      isLoading: false,
      isFiltered: false,
      isShowFilterModal: false,
      filterConditions: _.cloneDeep(BASE_FILTER_CONDITIONS),
      orders: [],
      position: 0,
      sortParams: {
        sortKey: "orderedAt",
        sortDirection: "DESC",
      },
      orderCountByStatus: {},
    };
  },
  created () {
    const { query } = this.$route;

    this.restoreHomeState();

    if (
      query &&
      query.status
    ) {
      this.status = query.status;
      this.filterConditions.status = this.status === "ALL" ? undefined : this.status;
    }
  },
  mounted () {
    this.fetchOrders();
  },
  methods: {
    /**
     * [restoreHomeState description]
     */
    restoreHomeState () {
      const homeState = sessionStorage.getItem("homeState");

      if (homeState) {
        const values = JSON.parse(homeState);
        const {
          status,
          currentPage,
          filterConditions,
          isFiltered,
          position,
        } = values;

        this.status = status;
        this.currentPage = currentPage;
        this.filterConditions = filterConditions;
        this.filterConditions.status = status === "ALL" ? undefined : status;
        this.isFiltered = isFiltered;
        this.position = position;

        sessionStorage.removeItem("homeState");
      }
    },

    /**
     * [keepHomeState description]
     * @param   {[type]}  position  [position description]
     */
    keepHomeState (position) {
      const {
        status,
        currentPage,
        filterConditions,
        isFiltered,
      } = this;
      const values = {
        status,
        currentPage,
        filterConditions,
        isFiltered,
        position,
      };

      sessionStorage.setItem("homeState", JSON.stringify(values));
    },

    /**
     * [checkUser description]
     * @return  {[type]}  [return description]
     */
    checkUser () {
      const user = this.$store.state.user;

      return !user || !user.sub;
    },

    /**
     * [fetchOrders description]
     */
    async fetchOrders () {
      const isUnavailableUser = this.checkUser();

      if (isUnavailableUser) {
        this.$notify.error(NOTIFY_MESSAGE_UNAVAILABLE_USER);
        return;
      }

      // 読み込み中にする
      this.isLoading = true;

      try {
        const payload = {
          q: this.filterConditions,
          page: this.currentPage,
          pageSize: PAGE_SIZE,
          sortKey: this.sortParams.sortKey,
          sortDirection: this.sortParams.sortDirection,
        };

        // 実行
        const res = await orderUsecase.getOrders(payload);
        this.getOrderByStatusFilter();

        // 注文一覧を更新
        this.update(res);
      } catch (err) {
        const onError = () => {};
        apiErrorHandler(this, err, onError);
      } finally {
        // 読み込み終了
        this.isLoading = false;

        _.defer(() => {
          this.$refs.adminHomeOrders.setPosition(this.position);
          this.position = 0;
        });
      }
    },

    /**
     * function that get Order counts by status
     * @returns {any}
     */
    async getOrderByStatusFilter () {
      const statuses = ORDER_STATUSES;
      const promises = [];
      statuses.forEach((status) => {
        const payload = {
          q: {
            ...this.filterConditions,
            status: status === "ALL" ? undefined : status,
          },
          status,
        };
        promises.push(orderUsecase.getOrderCount(payload));
      });
      try {
        const result = await Promise.all(promises);
        result.forEach((count, index) => {
          this.$set(this.orderCountByStatus, statuses[index], count);
        });
      } catch (e) {
        const onError = () => {};
        apiErrorHandler(this, e, onError);
      }
    },

    /**
     * [update description]
     * @param   {[type]}  res  [res description]
     */
    update (res) {
      this.total = res.total;
      this.totalPage = res.totalPage;
      this.orders = res.orders;
    },

    /**
     * [onChangeStatus description]
     */
    onChangeStatus () {
      const { status, filterConditions } = this;

      filterConditions.status = status === "ALL" ? undefined : status;
      this.currentPage = 1;

      this.$nextTick(() => {
        this.fetchOrders();
      });
    },

    /**
     * [onChangeCurrentPage description]
     */
    onChangeCurrentPage () {
      this.$nextTick(() => {
        this.fetchOrders();
      });
    },

    /**
     * [onChangeFilterCondition description]
     */
    onChangeFilterConditions (filterConditions) {
      this.isFiltered = true;
      this.filterConditions = filterConditions;

      this.$nextTick(() => {
        this.onChangeStatus();
      });
    },

    /**
     * [onResetFilterConditions description]
     */
    onResetFilterConditions () {
      this.isFiltered = false;
      this.filterConditions = _.cloneDeep(BASE_FILTER_CONDITIONS);
    },

    /**
     * [onClearFilterConditions description]
     */
    onClearFilterConditions () {
      // 条件をクリア時に AdminHomeFilter の検索条件もリセットする
      // 以下で onResetFilterConditions も走る
      this.$refs.filter.reset(true);

      this.$nextTick(() => {
        this.onChangeStatus();
      });
    },

    /**
     * [updateSortDirection description]
     * @param   {[type]}  sortParams  [sortParams description]
     */
    updateSortDirection (sortParams) {
      this.sortParams.sortKey = sortParams.sortKey; // || "orderedAt";
      this.sortParams.sortDirection = sortParams.sortDirection; // || "DESC";
      this.currentPage = 1;
      this.$nextTick(() => {
        this.fetchOrders();
      });
    },
  },
};

const orderUsecase = new OrderUsecase();
</script>

<style lang="scss" scoped>
@import "~@/assets/styles/variables.scss";

.admin-home {
  display: grid;
  height: calc(100% - 63px);
  grid-template-rows: auto auto 1fr auto;
  grid-template-columns: 1fr;
}
</style>
